import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { getIframeObservable_DO_NOT_USE } from '@atlassian/embedded-confluence/getIframeObservable';

import { expValEquals } from '@confluence/feature-experiments';

const WINDOW_MS = 10_000;
// 30 propsUpdated within 10s signals a runaway re-render loop (PIR-301897 / HOT-303389).
const THRESHOLD = 30;
// Cap to one event per minute so a runaway episode doesn't itself flood analytics.
const COOLDOWN_MS = 60_000;

type EmbeddedPageMode = 'view' | 'edit';

export const usePropsUpdatedFrequencyMonitor = (embeddedPageMode: EmbeddedPageMode): void => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const timestampsRef = useRef<number[]>([]);
	// -Infinity so the first anomaly isn't suppressed by the cooldown check.
	const lastFiredRef = useRef<number>(-Infinity);

	useEffect(() => {
		if (!expValEquals('embedded_confluence_props_anomaly_monitoring', 'isEnabled', true)) {
			return;
		}

		const observable = getIframeObservable_DO_NOT_USE();
		if (!observable) {
			return;
		}

		const onPropsUpdated = () => {
			const now = Date.now();
			const timestamps = timestampsRef.current;
			timestamps.push(now);

			while (timestamps.length > 0 && now - timestamps[0] > WINDOW_MS) {
				timestamps.shift();
			}

			if (timestamps.length < THRESHOLD || now - lastFiredRef.current < COOLDOWN_MS) {
				return;
			}
			lastFiredRef.current = now;

			createAnalyticsEvent({
				eventType: 'operational',
				action: 'detected',
				actionSubject: 'embeddedConfluencePropsUpdatedAnomaly',
				attributes: {
					embeddedPageMode,
					count: timestamps.length,
					windowMs: WINDOW_MS,
					ratePerMinute: Math.round((timestamps.length / WINDOW_MS) * 60_000),
					threshold: THRESHOLD,
				},
			}).fire('fabric-elements');
		};

		return observable.subscribe(onPropsUpdated);
	}, [createAnalyticsEvent, embeddedPageMode]);
};
